import { SELECTED_COUNTRY, SUCCESS_CODE } from "constants/AppConstants";
import { encryptAES, decryptAES } from "../../src/utils/aesDycrypt";
import { env } from "configs/EnvironmentConfig";
import { message } from "antd";

const { AES_KEY } = env;
class Utils {
  /**
   * Get first character from first & last sentences of a username
   * @param {String} name - Username
   * @return {String} 2 characters string
   */
  static getNameInitial(name) {
    let initials = name.match(/\b\w/g) || [];
    return ((initials.shift() || "") + (initials.pop() || "")).toUpperCase();
  }

  /**
   * Get current path related object from Navigation Tree
   * @param {Array} navTree - Navigation Tree from directory 'configs/NavigationConfig'
   * @param {String} path - Location path you looking for e.g '/app/dashboards/analytic'
   * @return {Object} object that contained the path string
   */
  static getRouteInfo(navTree, path) {
    if (navTree.path === path) {
      return navTree;
    }
    let route;
    for (let p in navTree) {
      if (navTree.hasOwnProperty(p) && typeof navTree[p] === "object") {
        route = this.getRouteInfo(navTree[p], path);
        if (route) {
          return route;
        }
      }
    }
    return route;
  }

  /**
   * Get accessible color contrast
   * @param {String} hex - Hex color code e.g '#3e82f7'
   * @return {String} 'dark' or 'light'
   */
  static getColorContrast(hex) {
    if (!hex) {
      return "dark";
    }
    const threshold = 130;
    const hRed = hexToR(hex);
    const hGreen = hexToG(hex);
    const hBlue = hexToB(hex);
    function hexToR(h) {
      return parseInt(cutHex(h).substring(0, 2), 16);
    }
    function hexToG(h) {
      return parseInt(cutHex(h).substring(2, 4), 16);
    }
    function hexToB(h) {
      return parseInt(cutHex(h).substring(4, 6), 16);
    }
    function cutHex(h) {
      return h.charAt(0) === "#" ? h.substring(1, 7) : h;
    }
    const cBrightness = (hRed * 299 + hGreen * 587 + hBlue * 114) / 1000;
    if (cBrightness > threshold) {
      return "dark";
    } else {
      return "light";
    }
  }

  /**
   * Darken or lighten a hex color
   * @param {String} color - Hex color code e.g '#3e82f7'
   * @param {Number} percent - Percentage -100 to 100, positive for lighten, negative for darken
   * @return {String} Darken or lighten color
   */
  static shadeColor(color, percent) {
    let R = parseInt(color.substring(1, 3), 16);
    let G = parseInt(color.substring(3, 5), 16);
    let B = parseInt(color.substring(5, 7), 16);
    R = parseInt((R * (100 + percent)) / 100);
    G = parseInt((G * (100 + percent)) / 100);
    B = parseInt((B * (100 + percent)) / 100);
    R = R < 255 ? R : 255;
    G = G < 255 ? G : 255;
    B = B < 255 ? B : 255;
    const RR =
      R.toString(16).length === 1 ? `0${R.toString(16)}` : R.toString(16);
    const GG =
      G.toString(16).length === 1 ? `0${G.toString(16)}` : G.toString(16);
    const BB =
      B.toString(16).length === 1 ? `0${B.toString(16)}` : B.toString(16);
    return `#${RR}${GG}${BB}`;
  }

  /**
   * Convert RGBA to HEX
   * @param {String} rgba - RGBA color code e.g 'rgba(197, 200, 198, .2)')'
   * @return {String} HEX color
   */
  static rgbaToHex(rgba) {
    const trim = (str) => str.replace(/^\s+|\s+$/gm, "");
    const inParts = rgba.substring(rgba.indexOf("(")).split(","),
      r = parseInt(trim(inParts[0].substring(1)), 10),
      g = parseInt(trim(inParts[1]), 10),
      b = parseInt(trim(inParts[2]), 10),
      a = parseFloat(
        trim(inParts[3].substring(0, inParts[3].length - 1))
      ).toFixed(2);
    const outParts = [
      r.toString(16),
      g.toString(16),
      b.toString(16),
      Math.round(a * 255)
        .toString(16)
        .substring(0, 2),
    ];

    outParts.forEach(function (part, i) {
      if (part.length === 1) {
        outParts[i] = "0" + part;
      }
    });
    return `#${outParts.join("")}`;
  }

  /**
   * Returns either a positive or negative
   * @param {Number} number - number value
   * @param {any} positive - value that return when positive
   * @param {any} negative - value that return when negative
   * @return {any} positive or negative value based on param
   */
  static getSignNum(number, positive, negative) {
    if (number > 0) {
      return positive;
    }
    if (number < 0) {
      return negative;
    }
    return null;
  }

  /**
   * Returns either ascending or descending value
   * @param {Object} a - antd Table sorter param a
   * @param {Object} b - antd Table sorter param b
   * @param {String} key - object key for compare
   * @return {any} a value minus b value
   */
  static antdTableSorter(a, b, key) {
    if (typeof a[key] === "number" && typeof b[key] === "number") {
      return a[key] - b[key];
    }

    if (typeof a[key] === "string" && typeof b[key] === "string") {
      a = a[key].toLowerCase();
      b = b[key].toLowerCase();
      return a > b ? -1 : b > a ? 1 : 0;
    }
    return;
  }

  /**
   * Filter array of object
   * @param {Array} list - array of objects that need to filter
   * @param {String} key - object key target
   * @param {any} value  - value that excluded from filter
   * @return {Array} a value minus b value
   */
  static filterArray(list, key, value) {
    let data = list;
    if (list) {
      data = list.filter((item) => item[key] === value);
    }
    return data;
  }

  /**
   * Remove object from array by value
   * @param {Array} list - array of objects
   * @param {String} key - object key target
   * @param {any} value  - target value
   * @return {Array} Array that removed target object
   */
  static deleteArrayRow(list, key, value) {
    let data = list;
    if (list) {
      data = list.filter((item) => item[key] !== value);
    }
    return data;
  }

  /**
   * Wild card search on all property of the object
   * @param {Number | String} input - any value to search
   * @param {Array} list - array for search
   * @return {Array} array of object contained keyword
   */
  static wildCardSearch(list, input) {
    const searchText = (item) => {
      for (let key in item) {
        if (item[key] == null) {
          continue;
        }
        if (
          item[key]
            .toString()
            .toUpperCase()
            .indexOf(input.toString().toUpperCase()) !== -1
        ) {
          return true;
        }
      }
    };
    list = list.filter((value) => searchText(value));
    return list;
  }

  /**
   * Get Breakpoint
   * @param {Object} screens - Grid.useBreakpoint() from antd
   * @return {Array} array of breakpoint size
   */
  static getBreakPoint(screens) {
    let breakpoints = [];
    for (const key in screens) {
      if (screens.hasOwnProperty(key)) {
        const element = screens[key];
        if (element) {
          breakpoints.push(key);
        }
      }
    }
    return breakpoints;
  }

  static filterHeaderParams = (obj) => {
    const result = {};

    Object.entries(obj).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        return; // Skip null/undefined values
      }
      if (Array.isArray(value)) {
        const filteredArray = value.filter(
          (item) => item !== null && item !== undefined
        );

        if (filteredArray.length > 0) {
          const cleanKey = key.replace(/\[\]$/, "");
          result[cleanKey] = filteredArray;
        }
      } else {
        // Non-array values are added directly
        result[key] = value;
      }
    });

    return result;
  };

  /**
   * Converts any data structure to FormData
   * Supports: objects, arrays, primitives, nested structures, Files, Blobs
   * Always returns FormData with all fields included
   * @param {any} data - The data to convert to FormData
   * @param {string} parentKey - Internal parameter for nested keys
   * @param {FormData} formData - Internal parameter for recursion
   * @returns {FormData} - Always returns FormData object
   */
  static toFormData(data, parentKey = "", formData = new FormData()) {
    // Handle null and undefined - always append as empty string
    if (data === null || data === undefined) {
      if (parentKey) {
        formData.append(parentKey, "");
      }
      return formData;
    }

    // Handle File and Blob objects
    if (data instanceof File || data instanceof Blob) {
      formData.append(parentKey, data);
      return formData;
    }

    // Handle primitive types (string, number, boolean)
    if (typeof data !== "object") {
      formData.append(parentKey, String(data));
      return formData;
    }

    // Handle Date objects
    if (data instanceof Date) {
      formData.append(parentKey, data.toISOString());
      return formData;
    }

    // Handle Arrays
    if (Array.isArray(data)) {
      data.forEach((item, index) => {
        const key = parentKey ? `${parentKey}[${index}]` : `[${index}]`;
        Utils.toFormData(item, key, formData);
      });
      return formData;
    }

    // Handle Objects
    Object.keys(data).forEach((key) => {
      const value = data[key];
      const formKey = parentKey ? `${parentKey}[${key}]` : key;
      Utils.toFormData(value, formKey, formData);
    });

    return formData;
  }

  // ================================================================
  // 🔒  ASL PARAM-LEVEL ENCRYPTION HELPERS
  // ================================================================
  /**
   * Encrypt a scalar value (id, action, etc.) for use in query strings.
   * Always returns a Base64 string generated by AES-CBC.
   * @param {String|Number} value  Plain value you want to protect
   * @returns {Promise<String>}    Base64( IV ∥ ciphertext )
   */
  static encryptASL = async (value) => {
    if (value === null || value === undefined) return value;
    // Convert everything to string so decrypt side is predictable
    const plain = String(value);
    return encryptAES(plain, AES_KEY);
  };

  /**
   * Decrypt a value produced by encryptASL().
   * @param {String} encryptedBase64  The Base64 string from the URL
   * @returns {Promise<String>}       The original plain value as string
   */
  static decryptASL = async (encryptedBase64) => {
    if (!encryptedBase64) return encryptedBase64;
    return decryptAES(encryptedBase64, AES_KEY);
  };

  /**
   * Convenience: take a plain params object and return an object whose
   * values are ASL-encrypted.  Undefined/null values are stripped out
   * the same way `filterParams` does.
   * @example
   *    Utils.encryptParams({ place_id: 17, page: 1 })
   *       → { place_id: "qB9z…==", page: "4n2P…==" }
   */
  static encryptParams = async (params = {}) => {
    const plain = Utils.filterParams(params);
    const encrypted = {};
    for (const [k, v] of Object.entries(plain)) {
      encrypted[k] = await Utils.encryptASL(v);
    }
    return encrypted;
  };

  /**
   * Enhanced version with additional options
   * Always includes all fields in FormData
   * @param {any} data - The data to convert
   * @param {Object} options - Configuration options
   * @param {boolean} options.skipNullValues - Skip null/undefined values (default: false)
   * @param {boolean} options.skipEmptyArrays - Skip empty arrays (default: false)
   * @param {boolean} options.skipEmptyObjects - Skip empty objects (default: false)
   * @param {string} options.arrayIndexFormat - Format for array indices ('brackets' or 'dot')
   * @returns {FormData} - Always returns FormData object
   */
  static toFormDataAdvanced(data, options = {}) {
    const {
      skipNullValues = false,
      skipEmptyArrays = false,
      skipEmptyObjects = false,
      arrayIndexFormat = "brackets", // 'brackets' or 'dot'
    } = options;

    function convert(data, parentKey = "", formData = new FormData()) {
      // Handle null and undefined - always include as empty string unless skipNullValues is true
      if (data === null || data === undefined) {
        if (!skipNullValues && parentKey) {
          formData.append(parentKey, "");
        }
        return formData;
      }

      // Handle File and Blob objects
      if (data instanceof File || data instanceof Blob) {
        formData.append(parentKey, data);
        return formData;
      }

      // Handle primitive types
      if (typeof data !== "object") {
        formData.append(parentKey, String(data));
        return formData;
      }

      // Handle Date objects
      if (data instanceof Date) {
        formData.append(parentKey, data.toISOString());
        return formData;
      }

      // Handle Arrays
      if (Array.isArray(data)) {
        if (skipEmptyArrays && data.length === 0) {
          return formData;
        }

        data.forEach((item, index) => {
          const key = parentKey
            ? arrayIndexFormat === "dot"
              ? `${parentKey}.${index}`
              : `${parentKey}[${index}]`
            : `[${index}]`;
          convert(item, key, formData);
        });
        return formData;
      }

      // Handle Objects
      const keys = Object.keys(data);
      if (skipEmptyObjects && keys.length === 0) {
        return formData;
      }

      keys.forEach((key) => {
        const value = data[key];
        const formKey = parentKey ? `${parentKey}[${key}]` : key;
        convert(value, formKey, formData);
      });

      return formData;
    }

    return convert(data);
  }
  static getDefaultCountry() {
    try {
      const item = localStorage.getItem(SELECTED_COUNTRY);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(
        `Error getting "${SELECTED_COUNTRY}" from localStorage:`,
        error
      );
      return null;
    }
  }
  /**
   * Converts text to lowercase and removes spaces and special characters
   * @param {string} text - The input text to format
   * @param {string} replacement - Character to replace spaces/special chars with (default: '_')
   * @returns {string} - Formatted text
   */
  static formatTextClean = (text, replacement = "_") => {
    if (!text || typeof text !== "string") return "";

    return text
      .toLowerCase() // Convert to lowercase
      .trim() // Remove leading/trailing spaces
      .replace(/[^a-z0-9]/g, replacement) // Replace special chars and spaces
      .replace(new RegExp(`${replacement}+`, "g"), replacement) // Replace multiple replacements with single
      .replace(new RegExp(`^${replacement}|${replacement}$`, "g"), ""); // Remove leading/trailing replacements
  };

  static getStatusResponse = async (
    dispatch,
    actionCreator,
    {
      actionPayload = {},
      showMessage = false,
      fetchFunction = null,
      fetchPayload = null,
    } = {}
  ) => {
    try {
      const result = await dispatch(actionCreator(actionPayload)).unwrap();

      if (result.status.status_code === SUCCESS_CODE) {
        if (showMessage) {
          message.success(result.status.message);
        }

        if (fetchFunction) {
          try {
            dispatch(fetchFunction(fetchPayload));
          } catch (fetchError) {
            console.warn("Auto-refresh failed:", fetchError);
          }
        }

        return {
          status: true,
          message: result.status.message,
          data: result.data || null,
        };
      } else {
        if (showMessage) {
          message.error(result.status.message);
        }
        return {
          status: false,
          message: result.status.message,
          error: result.status.error || null,
        };
      }
    } catch (error) {
      // Show error message if requested
      if (showMessage) {
        message.error(error.message || "An unexpected error occurred");
      }
      return {
        status: false,
        message: error.message || "An unexpected error occurred",
        error: error,
      };
    }
  };
  /**
   * Decodes a JWT token and extracts the role_id from the payload
   * @param {string} token - The JWT token to decode
   * @returns {string|null} - The role_id from the token payload, or null if not found/invalid
   */
  static decodeJWTAndGetRole(token) {
    console.warn(token, "token");
    try {
      // // Check if token is provided
      // if (!token || typeof token !== "string") {
      //   throw new Error("Invalid token provided");
      // }

      // Remove 'Bearer ' prefix if present
      const cleanToken = token.replace(/^Bearer\s+/i, "");

      // Split the token into parts
      const tokenParts = cleanToken.split(".");

      // JWT should have 3 parts: header.payload.signature
      if (tokenParts.length !== 3) {
        throw new Error("Invalid JWT format");
      }

      // Decode the payload (second part)
      const payload = tokenParts[1];

      // Add padding if necessary for base64 decoding
      const paddedPayload =
        payload + "=".repeat((4 - (payload.length % 4)) % 4);

      // Decode base64
      const decodedPayload = atob(paddedPayload);

      // Parse JSON
      const payloadObj = JSON.parse(decodedPayload);

      // Extract role_id from the payload
      const roleId = payloadObj.role_id;
      console.log("Decoded role_id:", roleId);
      return roleId || null;
    } catch (error) {
      console.error("Error decoding JWT:", error.message);
      return null;
    }
  }
  /**
   * Safely parses data from localStorage
   * @param {string} key - The localStorage key to retrieve
   * @param {any} defaultValue - Default value to return if parsing fails or key doesn't exist (default: null)
   * @returns {any} - Parsed data or default value
   */
  static getFromLocalStorage = (key, defaultValue = null) => {
    try {
      const data = localStorage.getItem(key);

      // Check if data exists and is not string representations of undefined/null
      if (data && data !== "undefined" && data !== "null") {
        return JSON.parse(data);
      }

      return defaultValue;
    } catch (error) {
      console.error(
        `Error parsing data from localStorage for key "${key}":`,
        error
      );
      return defaultValue;
    }
  };

  /**
 * Capitalizes the first letter of each word in a string
 * @param {string} str - The string to capitalize
 * @returns {string} The string with each word capitalized
 */
  static capitalizeWords = (str) => {
    if (typeof str !== 'string' || str.length === 0) {
      return str;
    }
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}

export default Utils;
